import * as THREE from 'three';

// Re-defining these here to decouple from any specific store
export type LayoutShape = 'sphere' | 'torus' | 'cylinder';
export type LayoutMode = 'solid_shape' | 'fixed_particle_size';

export interface LayoutParams {
  count: number;
  shape: LayoutShape;
  mode: LayoutMode;
  shapeSize: number;      // e.g., radius of sphere/torus/cylinder
  particleSize: number;   // size for fixed_particle_size mode
}

export interface Layout {
    positions: THREE.Vector3[];
    scales: number[];
}

export function getLayout(params: LayoutParams): Layout {
    switch(params.shape) {
        case 'sphere':
            return generateSphereLayout(params);
        case 'torus':
            return generateTorusLayout(params);
        case 'cylinder':
            return generateCylinderLayout(params);
        default:
            return { positions: [], scales: [] };
    }
}


function generateSphereLayout(params: LayoutParams): Layout {
    const { count, mode, shapeSize, particleSize } = params;
    
    const positions: THREE.Vector3[] = [];
    const scales: number[] = [];
    if (count <= 0) return { positions, scales };
    
    let radius = 1;
    let scale = 1;

    if (mode === 'solid_shape') {
        radius = shapeSize;
        const sphereSurfaceArea = 4 * Math.PI * radius * radius;
        const areaPerParticle = sphereSurfaceArea / count;
        // The scale is the diameter of the circular area for one particle
        scale = Math.sqrt(areaPerParticle / Math.PI) * 2;

    } else { // mode === 'fixed_particle_size'
        const particleArea = Math.PI * (particleSize / 2) * (particleSize / 2);
        const totalSurfaceArea = particleArea * count;
        radius = Math.sqrt(totalSurfaceArea / (4 * Math.PI));
        scale = particleSize;
    }

    const goldenAngle = Math.PI * (3 - Math.sqrt(5));

    for (let i = 0; i < count; i++) {
        const y = 1 - (i / (count - 1)) * 2; 
        const radiusAtY = Math.sqrt(1 - y * y);

        const theta = goldenAngle * i;

        const x = Math.cos(theta) * radiusAtY;
        const z = Math.sin(theta) * radiusAtY;

        positions.push(new THREE.Vector3(x, y, z).multiplyScalar(radius));
        scales.push(scale);
    }

    return { positions, scales };
}

function generateTorusLayout(params: LayoutParams): Layout {
    const { count, shapeSize } = params;
    const positions: THREE.Vector3[] = [];
    const scales: number[] = new Array(count).fill(0.1); // TODO: Implement proper scaling
    if (count <= 0) return { positions, scales };

    const tubeRadius = shapeSize * 0.4;

    const numU = Math.round(Math.sqrt(count * (shapeSize / tubeRadius)));
    const numV = Math.ceil(count / numU);

    for (let i = 0; i < count; i++) {
        const u_index = i % numU;
        const v_index = Math.floor(i / numU);

        const u = (u_index / numU) * 2 * Math.PI;
        const v = (v_index / numV) * 2 * Math.PI;

        const x = (shapeSize + tubeRadius * Math.cos(v)) * Math.cos(u);
        const y = (shapeSize + tubeRadius * Math.cos(v)) * Math.sin(u);
        const z = tubeRadius * Math.sin(v);
        
        positions.push(new THREE.Vector3(x, y, z));
    }
    return { positions, scales };
}


function generateCylinderLayout(params: LayoutParams): Layout {
    const { count, shapeSize } = params;
    const positions: THREE.Vector3[] = [];
    const scales: number[] = new Array(count).fill(0.1); // TODO: Implement proper scaling
    if (count <= 0) return { positions, scales };
    
    const height = shapeSize * 2;

    const capRatio = 0.15;
    const topCapCount = Math.floor(count * capRatio);
    const bottomCapCount = Math.floor(count * capRatio);
    const sideCount = count - topCapCount - bottomCapCount;
    
    // Side
    for (let i = 0; i < sideCount; i++) {
        const angle = (i / sideCount) * 2 * Math.PI;
        const y = (Math.random() - 0.5) * height; // Distribute along height
        const x = Math.cos(angle) * shapeSize;
        const z = Math.sin(angle) * shapeSize;
        positions.push(new THREE.Vector3(x, y, z));
    }
    // Top Cap
    for (let i = 0; i < topCapCount; i++) {
        const r = Math.sqrt(Math.random()) * shapeSize;
        const angle = Math.random() * 2 * Math.PI;
        const y = height / 2;
        const x = Math.cos(angle) * r;
        const z = Math.sin(angle) * r;
        positions.push(new THREE.Vector3(x, y, z));
    }
    // Bottom Cap
    for (let i = 0; i < bottomCapCount; i++) {
        const r = Math.sqrt(Math.random()) * shapeSize;
        const angle = Math.random() * 2 * Math.PI;
        const y = -height / 2;
        const x = Math.cos(angle) * r;
        const z = Math.sin(angle) * r;
        positions.push(new THREE.Vector3(x, y, z));
    }

    return { positions, scales };
}

// In the future, other layout functions can be added here:
// export function generatePlaneLayout(...) {}
// export function generateCubeLayout(...) {} 